import React from 'react';
import { Shield, Lock, Eye, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { salonInfo } from '../data/mock';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#EBF4F6] via-white to-[#EBF4F6]/50 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Badge className="mb-6 bg-[#3D7A8C]/10 text-[#3D7A8C] border-0 px-5 py-2 text-sm font-medium">
            Legal Information
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-3">Introduction</h2>
                <p className="text-gray-700 leading-relaxed">
                  <strong>{salonInfo.registeredName}</strong>, operating under the brand name &ldquo;<strong>{salonInfo.brandName}</strong>&rdquo; (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Information We Collect */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
                
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Personal Information</h3>
                <p className="text-gray-700 mb-4">When you use our services or contact us, we may collect:</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Name and contact information (email, phone number)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Service preferences and history</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Payment information (processed securely through third-party payment processors)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Any additional information you provide to us</span>
                  </li>
                </ul>

                <h3 className="font-semibold text-lg text-gray-900 mb-3">Automatically Collected Information</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">IP address and browser type</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Device information and operating system</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Usage data and analytics</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How We Use Your Information */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <Eye className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">We use the information we collect to:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Provide and manage our services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Communicate with you about our services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Send you promotional offers and updates (with your consent)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Improve our website and services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Comply with legal obligations</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Access your personal information</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Correct inaccurate information</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Request deletion of your data</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Opt-out of marketing communications</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="border-2 border-[#3D7A8C]/30 rounded-2xl bg-gradient-to-br from-[#EBF4F6] to-[#D7E9ED]/50">
          <CardContent className="p-8 text-center">
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Questions About This Policy?</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Brand:</strong> {salonInfo.brandName}</p>
              <p><strong>Registered Company:</strong> {salonInfo.registeredName}</p>
              <p><strong>Email:</strong> <a href={`mailto:${salonInfo.email}`} className="text-[#3D7A8C] hover:text-[#356A7A]">{salonInfo.email}</a></p>
              <p><strong>Phone:</strong> <a href={`tel:${salonInfo.phone}`} className="text-[#3D7A8C] hover:text-[#356A7A]">{salonInfo.phone}</a></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
